import { pageMetadata, syncMetadata } from "../lib/seo";
import SmartLeadImage from "./SmartLeadImage";
import { mapPost, mapAuthor, fetchArchive, fetchWordPressAuthor } from "../lib/wordpress";
import NewsSidebar from "./NewsSidebar";
import { titleCaseHeadline } from "../utils/titleCase";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Link } from "./CanonicalLink";
import { Home, ChevronDown } from "lucide-react";
import TopBar from "./TopBar";
import Masthead from "./Masthead";
import Footer from "./Footer";
import Reveal from "./Reveal";
import { authorSlug, getAuthorName, getStoriesByAuthor, mergeAuthorPosts } from "../data/stories";

export const initialsOf = (name) =>
  (name || "?")
    .split(" ")
    .filter(Boolean)
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

const AuthorStoryCard = ({ story, lead = false }) => (
  <article className="group h-full" data-testid={`author-story-${story.id}`}>
    <Link to={`/${story.id}`} className="block h-full">
      {story.img && (
        <div className="img-frame overflow-hidden aspect-[16/9] bg-[var(--card-muted)]">
          {lead ? <SmartLeadImage story={story} /> : <img
            src={story.img}
            alt={story.imgAlt || story.title}
            loading="lazy"
            className="w-full h-full object-cover"
          />}
        </div>
      )}
      <h3 className={`mt-3 font-display ${lead ? "text-3xl" : "text-lg"} leading-snug font-semibold line-clamp-3 group-hover:text-[var(--accent)] transition-colors duration-300`}>
        {titleCaseHeadline(story.title)}
      </h3>
      {lead && story.excerpt && (
        <p className="mt-2 text-sm leading-relaxed text-[var(--muted)] line-clamp-3">{story.excerpt}</p>
      )}
      <p className="mt-2 text-[11px] text-[var(--faint)]">{story.date}</p>
    </Link>
  </article>
);

export const AuthorPage = () => {
  const { slug } = useParams();
  const [author, setAuthor] = useState(null);
  const name = getAuthorName(slug) || author?.name;
  const [archive, setArchive] = useState(null);
  const stories = archive?.slug === slug ? archive.posts : name ? getStoriesByAuthor(slug) : [];
  const [visible, setVisible] = useState(15);
  useEffect(() => setVisible(15), [slug]);

  useEffect(() => {
    if (!slug) return undefined;
    let live = true;
    setAuthor(null);
    setArchive(null);
    const controller = new AbortController();
    fetchWordPressAuthor(slug).then(wpAuthor => {
      const a = wpAuthor ? mapAuthor(wpAuthor) : null;
      if (live && a) setAuthor(a);
      if (!live || !a?.id || a.id === 5) return;
      fetchArchive({author:a.id,perPage:50}).then(result => { if(live) {mergeAuthorPosts(result.posts);setArchive({slug,posts:result.posts.map(mapPost),total:result.total});} }).catch(() => {});
    }).catch(() => {});
    return () => {
      live = false;
      controller.abort();
    };
  }, [slug]);

  const [moreLoading,setMoreLoading] = useState(false);
  const showMore = async () => {
    if(visible < stories.length) {setVisible(count=>count+10);return;}
    if(!author?.id || moreLoading) return;
    setMoreLoading(true);
    try {const result=await fetchArchive({author:author.id,offset:stories.length,perPage:50});mergeAuthorPosts(result.posts);setArchive({slug,posts:[...stories,...result.posts.map(mapPost)],total:result.total});setVisible(count=>count+10);} catch { /* Existing stories remain available if WordPress is temporarily unreachable. */ } finally {setMoreLoading(false);}
  };
  useEffect(()=>{
    if(name)syncMetadata(pageMetadata({pathname:window.location.pathname,author:{...author,name}}));
  },[name,author]);

  if (!name) {
    return (
      <div data-testid="author-not-found">
        <TopBar />
        <Masthead />
        <div className="mx-auto max-w-[1440px] px-6 py-24 text-center">
          <h1 className="font-display text-4xl">Author not found</h1>
          <p className="mt-3 text-[var(--muted)]">
            We couldn't find this byline in our newsroom.
          </p>
          <Link
            to="/"
            data-testid="author-not-found-home-link"
            className="mt-6 inline-flex items-center gap-2 text-[12px] font-bold uppercase tracking-[0.18em] text-[var(--accent)]"
          >
            <Home size={14} /> Back to the front page
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div data-testid="author-page">
      <TopBar />
      <Masthead />

      <main>
        <section className="bg-[var(--bg)]">
          <div className="mx-auto max-w-[1440px] px-4 sm:px-6 pt-10 sm:pt-14">
            <Reveal>
              <div className="rounded-[10px] overflow-hidden border-t-[3px] border-[#cf0a0a] bg-[var(--bg-soft)] px-5 sm:px-7 py-8 flex flex-wrap items-center gap-6">
                {author && author.has_avatar ? (
                  <img
                    src={author.avatar}
                    onError={() => setAuthor(current => current ? { ...current, has_avatar: false } : current)}
                    alt={name}
                    data-testid="author-avatar-img"
                    className="w-24 h-24 sm:w-28 sm:h-28 rounded-full object-cover ring-1 ring-black/10 shrink-0"
                  />
                ) : (
                  <span
                    data-testid="author-avatar"
                    className="w-24 h-24 sm:w-28 sm:h-28 rounded-full bg-[#f7e7e5] text-[#cf0a0a] flex items-center justify-center text-[30px] font-extrabold tracking-wide shrink-0"
                  >
                    {initialsOf(name)}
                  </span>
                )}
                <div className="min-w-0">
                  <h1
                    data-testid="author-name"
                    className="font-display text-3xl sm:text-4xl font-semibold"
                  >
                    {name}
                  </h1>
                  <p className="mt-1 text-[12px] text-[var(--faint)]" data-testid="author-label">The Standard Author</p>
                  {author && author.bio && (
                    <p
                      className="mt-3 max-w-2xl text-[14px] leading-7 text-[var(--muted)]"
                      data-testid="author-bio"
                    >
                      {author.bio}
                    </p>
                  )}
                </div>
              </div>
            </Reveal>
          </div>
        </section>

        {stories.length > 0 ? (
          <section className="mx-auto max-w-[1440px] px-4 sm:px-6 pt-10 pb-16">
            <h2 className="title-black font-display text-2xl font-bold mb-6">Stories By {name}</h2>
            <div className="grid gap-8 lg:grid-cols-2" aria-label="Featured author stories">
              <AuthorStoryCard story={stories[0]} lead />
              <div className="grid grid-cols-2 gap-x-6 gap-y-8">
                {stories.slice(1,5).map(story => <AuthorStoryCard key={story.id} story={story} />)}
              </div>
            </div>
            <div className="mt-10 grid items-start gap-8 lg:grid-cols-[minmax(0,1fr)_340px]">
              <div className="min-w-0">
                <div className="grid grid-cols-2 gap-x-4 sm:gap-x-6 gap-y-8">
                  {stories.slice(5,visible).map(story => <AuthorStoryCard key={story.id} story={story} />)}
                </div>
                {visible < (archive?.total || stories.length) && <button type="button" disabled={moreLoading} onClick={showMore} className="mt-8 flex w-full items-center justify-center gap-2 rounded-[5px] bg-[#cf0a0a] py-4 font-bold text-white cursor-pointer hover:bg-[#141618] disabled:cursor-wait disabled:opacity-70 transition-colors">{moreLoading ? "Loading…" : "Load More"} <ChevronDown size={16} aria-hidden="true" /></button>}
              </div>
              <NewsSidebar sticky />
            </div>
          </section>
        ) : (
          <div className="mx-auto max-w-[1440px] px-4 sm:px-6 py-16 text-center text-[var(--muted)]">
            No stories from this byline yet.
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default AuthorPage;
